import React, { useRef, useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Tooltip } from "react-tooltip";
import {
  getAllMenusAndFeatures,
  getApplicationFeaturesAndMenus,
  updateFeatureClientMapping,
  updateMenuSequence,
  UPSERT_PENDING_MAPPING_CHANGE,
  REMOVE_PENDING_MAPPING_CHANGE,
  CLEAR_PENDING_MAPPING_CHANGES,
  PendingMappingChange,
} from "../../redux/slice/FeatureSlice";
import { AppDispatch, RootState } from "../../redux/store";
import "./CustomSettings.scss";
import {
  ApiFeature,
  ApiMenu,
  SettingItem,
  SubsectionState,
  applyPendingOverlayForTab,
  getVisibleMemberTypeFilters,
  groupPendingChangesForSave,
  isProminenceClientName,
  mergeApplicationFeaturePayloads,
  resolveMemberTypesForGet,
  resolveMemberTypesForSequenceSave,
  resolveMemberTypesForTab,
  sortSettingsByApiMenuOrder,
  toNumericId,
} from "./customSettingsUtils";

type DashboardSubsection = {
  id: string;
  label: string;
};

const isProminenceClient = isProminenceClientName(
  process.env.REACT_APP_ClientName,
);

const VISIBLE_MEMBER_TYPE_FILTERS =
  getVisibleMemberTypeFilters(isProminenceClient);

const DEFAULT_MEMBER_TYPE_TAB =
  VISIBLE_MEMBER_TYPE_FILTERS[0]?.id ?? "all";

const MENU_ICON_MAP: Record<string, string> = {
  dashboard: "fh_dashboard_fill",
  claims: "fh_summary",
  "provider directory": "fh_search_dr",
  "formulary directory": "fh_bottle_med_2",
  formulary: "fh_bottle_med_2",
  "resources & forms": "fh_profile",
  "clinical data": "fh_task_2_fill",
  "clinical information": "fh_task_2_fill",
  "prior authorization": "fh_thumb_up",
  "lab result": "fh_lab",
  lab: "fh_lab",
  "previous insurer": "fh_health_plan",
  "digital health card": "fh_credit_card",
  "digital card": "fh_credit_card",
  "out of pocket": "fh_add_task",
  "reference documents": "fh_docs_fill",
  "request management": "fh_case_manager",
  "coverage & benefits": "fh_care_hand",
  "my profile": "fh_user_fill",
  survey: "fh_audit_doc",
  "member id cards": "fh_id_card",
  "ict meeting": "fh_meet",
  "ict medical": "fh_meet",
  "quality care gaps": "fh_heart_fill",
};

const DEFAULT_MENU_ICON = "fh_docs_fill";

const pickFirst = (obj: Record<string, any>, keys: string[]) => {
  for (const key of keys) {
    if (obj[key] !== undefined && obj[key] !== null && obj[key] !== "") {
      return obj[key];
    }
  }
  return undefined;
};

const toId = (value: unknown): string => String(value ?? "").trim();

const parseFeaturesPayload = (rawFeatures: unknown): unknown[] => {
  if (Array.isArray(rawFeatures)) {
    return rawFeatures;
  }

  if (typeof rawFeatures !== "string") {
    return [];
  }

  const trimmed = rawFeatures.trim();
  if (!trimmed) {
    return [];
  }

  try {
    const parsed = JSON.parse(trimmed);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const normalizeFeatures = (rawFeatures: unknown): ApiFeature[] => {
  return parseFeaturesPayload(rawFeatures)
    .map((feature) => {
      if (!feature || typeof feature !== "object") {
        return null;
      }

      const item = feature as Record<string, any>;
      const id = toId(
        pickFirst(item, [
          "featureId",
          "FeatureId",
          "featureID",
          "FeatureID",
          "feature_Id",
          "id",
          "Id",
          "ID",
        ]),
      );
      const label = String(
        pickFirst(item, [
          "featureName",
          "FeatureName",
          "feature_Name",
          "name",
          "Name",
          "label",
          "Label",
          "title",
          "Title",
        ]) ?? "",
      ).trim();

      // Accept id-only or label-only rows from API
      if (!id && !label) {
        return null;
      }

      return { id: id || label, label: label || id };
    })
    .filter((feature): feature is ApiFeature => feature !== null);
};

const extractMenusPayload = (payload: unknown): unknown[] => {
  if (!payload) {
    return [];
  }

  if (Array.isArray(payload)) {
    return payload;
  }

  if (typeof payload !== "object") {
    return [];
  }

  const obj = payload as Record<string, any>;
  const nested = pickFirst(obj, [
    "data",
    "Data",
    "result",
    "Result",
    "menus",
    "Menus",
    "menusAndFeatures",
    "MenusAndFeatures",
    "items",
    "Items",
  ]);

  return Array.isArray(nested) ? nested : [];
};

const getNestedFeatures = (item: Record<string, any>): ApiFeature[] =>
  normalizeFeatures(
    pickFirst(item, [
      "features",
      "Features",
      "featureList",
      "FeatureList",
      "menuFeatures",
      "MenuFeatures",
      "children",
      "Children",
      "subFeatures",
      "SubFeatures",
    ]),
  );

const getRowFeature = (item: Record<string, any>): ApiFeature | null => {
  const featureId = toId(
    pickFirst(item, [
      "featureId",
      "FeatureId",
      "featureID",
      "FeatureID",
      "feature_Id",
    ]),
  );
  const featureLabel = String(
    pickFirst(item, [
      "featureName",
      "FeatureName",
      "feature_Name",
    ]) ?? "",
  ).trim();

  if (!featureId && !featureLabel) {
    return null;
  }

  return {
    id: featureId || featureLabel,
    label: featureLabel || featureId,
  };
};

const normalizeMenusAndFeatures = (payload: unknown): ApiMenu[] => {
  const rows = extractMenusPayload(payload);
  const menusByKey = new Map<string, ApiMenu>();

  const readSequence = (item: Record<string, any>) => {
    const raw = pickFirst(item, [
      "sequence",
      "Sequence",
      "displayOrder",
      "DisplayOrder",
      "menuSequence",
      "MenuSequence",
      "sortOrder",
      "SortOrder",
    ]);
    if (raw === undefined || raw === null || raw === "") {
      return undefined;
    }
    const value = Number(raw);
    return Number.isNaN(value) ? undefined : value;
  };

  const upsertMenu = (item: Record<string, any>) => {
    const id = toId(
      pickFirst(item, [
        "menuId",
        "MenuId",
        "menuID",
        "MenuID",
        "menu_Id",
        "id",
        "Id",
        "ID",
      ]),
    );
    const label = String(
      pickFirst(item, [
        "menuName",
        "MenuName",
        "menu_Name",
        "name",
        "Name",
        "label",
        "Label",
        "title",
        "Title",
      ]) ?? "",
    ).trim();

    if (!id && !label) {
      return;
    }

    // Prefer menuId/menuName for grouping when feature fields are also present
    const menuId = toId(
      pickFirst(item, ["menuId", "MenuId", "menuID", "MenuID", "menu_Id"]),
    );
    const menuLabel = String(
      pickFirst(item, ["menuName", "MenuName", "menu_Name"]) ?? "",
    ).trim();
    const resolvedId = menuId || id;
    const resolvedLabel = menuLabel || label;
    const menuKey = resolvedId || resolvedLabel.toLowerCase();
    const existing = menusByKey.get(menuKey);
    const nestedFeatures = getNestedFeatures(item);
    const rowFeature = getRowFeature(item);
    const sequence = readSequence(item);

    if (!existing) {
      const features = [...nestedFeatures];
      if (rowFeature) {
        features.push(rowFeature);
      }

      menusByKey.set(menuKey, {
        id: resolvedId || resolvedLabel,
        label: resolvedLabel || resolvedId,
        features,
        sequence,
      });
      return;
    }

    nestedFeatures.forEach((feature) => {
      if (
        !existing.features.some(
          (current) =>
            current.id === feature.id ||
            current.label.toLowerCase() === feature.label.toLowerCase(),
        )
      ) {
        existing.features.push(feature);
      }
    });

    if (
      rowFeature &&
      !existing.features.some(
        (current) =>
          current.id === rowFeature.id ||
          current.label.toLowerCase() === rowFeature.label.toLowerCase(),
      )
    ) {
      existing.features.push(rowFeature);
    }

    if (!existing.label && resolvedLabel) {
      existing.label = resolvedLabel;
    }

    if (
      sequence != null &&
      (existing.sequence == null || sequence < existing.sequence)
    ) {
      existing.sequence = sequence;
    }
  };

  // Keep GetAllMenusAndFeatures response order — Sequence can lag behind the
  // array order right after UpdateMenuSequence, and re-sorting by it undoes the
  // newly saved order in the UI.
  rows.forEach((row) => {
    if (!row || typeof row !== "object") {
      return;
    }
    upsertMenu(row as Record<string, any>);
  });

  // Some APIs return features in a separate top-level array
  if (payload && typeof payload === "object" && !Array.isArray(payload)) {
    const root = payload as Record<string, any>;
    const rootFeatures = pickFirst(root, [
      "features",
      "Features",
      "featureList",
      "FeatureList",
    ]);
    if (Array.isArray(rootFeatures)) {
      rootFeatures.forEach((feature) => {
        if (!feature || typeof feature !== "object") {
          return;
        }
        upsertMenu(feature as Record<string, any>);
      });
    }
  }

  return Array.from(menusByKey.values()).map((menu, index) => ({
    ...menu,
    sequence: index + 1,
  }));
};

const getMenuIcon = (label: string) =>
  MENU_ICON_MAP[label.trim().toLowerCase()] ?? DEFAULT_MENU_ICON;

const isDashboardMenu = (label: string) =>
  label.trim().toLowerCase() === "dashboard";

/** Dashboard always stays first; its sequence cannot change */
const pinDashboardFirst = (items: SettingItem[]): SettingItem[] => {
  const dashboardIndex = items.findIndex((item) =>
    isDashboardMenu(item.label),
  );
  if (dashboardIndex <= 0) {
    return items;
  }
  const next = [...items];
  const [dashboard] = next.splice(dashboardIndex, 1);
  next.unshift(dashboard);
  return next;
};

/** Matches Bootstrap breakpoints used by custom-settings grid cols */
const getSettingsGridColumnCount = () => {
  if (typeof window === "undefined") return 1;
  if (window.matchMedia("(min-width: 1200px)").matches) return 3; // col-xl-4
  if (window.matchMedia("(min-width: 768px)").matches) return 2; // col-md-6
  return 1;
};

/**
 * For mostly-vertical drags in a multi-column grid, snap to the same column
 * so side menus are not swapped by accident. Diagonal / horizontal keep the
 * hovered target so e.g. 4 can swap directly with 8.
 */
const resolveGridDragTargetId = (
  items: SettingItem[],
  sourceId: string,
  hoveredId: string,
  movement: { dx: number; dy: number },
): string => {
  if (!sourceId || !hoveredId || sourceId === hoveredId) {
    return hoveredId;
  }

  const columnCount = getSettingsGridColumnCount();
  if (columnCount <= 1) {
    return hoveredId;
  }

  const sourceIndex = items.findIndex((item) => item.id === sourceId);
  const hoverIndex = items.findIndex((item) => item.id === hoveredId);
  if (sourceIndex < 0 || hoverIndex < 0) {
    return hoveredId;
  }

  const absDx = Math.abs(movement.dx);
  const absDy = Math.abs(movement.dy);
  const isDiagonalIntent = absDx > 12 && absDy > 12;
  const isHorizontalIntent = absDx >= absDy && absDx > 2;

  // Horizontal or diagonal: use the actual hovered menu
  if (isDiagonalIntent || isHorizontalIntent) {
    return hoveredId;
  }

  const isVerticalIntent = absDy > absDx && absDy > 2;
  if (!isVerticalIntent) {
    return hoveredId;
  }

  const sourceColumn = sourceIndex % columnCount;
  const hoverRow = Math.floor(hoverIndex / columnCount);
  let adjustedIndex = hoverRow * columnCount + sourceColumn;

  if (adjustedIndex < 0 || adjustedIndex >= items.length) {
    return hoveredId;
  }

  // Never target Dashboard; move to next eligible slot in that column
  if (isDashboardMenu(items[adjustedIndex].label)) {
    adjustedIndex += columnCount;
    if (adjustedIndex >= items.length) {
      return hoveredId;
    }
  }

  return items[adjustedIndex]?.id ?? hoveredId;
};

const mapMenusToSettings = (
  menus: ApiMenu[],
  enabledMenuIds: Set<string> = new Set(),
  enabledMenuLabels: Set<string> = new Set(),
): SettingItem[] =>
  menus.map((menu) => ({
    id: menu.id,
    label: menu.label,
    icon: getMenuIcon(menu.label),
    enabled: isMenuEnabled(menu, enabledMenuIds, enabledMenuLabels),
    hasSubsections:
      isDashboardMenu(menu.label) && menu.features.length > 0,
  }));

const mapDashboardSubsections = (menus: ApiMenu[]): DashboardSubsection[] => {
  const dashboard = menus.find((menu) => isDashboardMenu(menu.label));
  if (!dashboard) {
    return [];
  }

  return dashboard.features.map((feature) => ({
    id: feature.id,
    label: feature.label,
  }));
};

const buildSubsectionState = (
  subsections: DashboardSubsection[],
  enabledFeatureIds: Set<string> = new Set(),
  enabledFeatureLabels: Set<string> = new Set(),
): SubsectionState =>
  subsections.reduce<SubsectionState>((acc, item) => {
    acc[item.id] = isFeatureEnabled(
      item,
      enabledFeatureIds,
      enabledFeatureLabels,
    );
    return acc;
  }, {});

const collectEnabledKeys = (menus: ApiMenu[]) => {
  const enabledMenuIds = new Set<string>();
  const enabledMenuLabels = new Set<string>();
  const enabledFeatureIds = new Set<string>();
  const enabledFeatureLabels = new Set<string>();

  menus.forEach((menu) => {
    enabledMenuIds.add(menu.id);
    enabledMenuLabels.add(menu.label.trim().toLowerCase());

    if (isDashboardMenu(menu.label)) {
      menu.features.forEach((feature) => {
        enabledFeatureIds.add(feature.id);
        enabledFeatureLabels.add(feature.label.trim().toLowerCase());
      });
    }
  });

  return {
    enabledMenuIds,
    enabledMenuLabels,
    enabledFeatureIds,
    enabledFeatureLabels,
  };
};

const isMenuEnabled = (
  menu: ApiMenu,
  enabledMenuIds: Set<string>,
  enabledMenuLabels: Set<string>,
) =>
  enabledMenuIds.has(menu.id) ||
  enabledMenuLabels.has(menu.label.trim().toLowerCase());

const isFeatureEnabled = (
  feature: DashboardSubsection,
  enabledFeatureIds: Set<string>,
  enabledFeatureLabels: Set<string>,
) =>
  enabledFeatureIds.has(feature.id) ||
  enabledFeatureLabels.has(feature.label.trim().toLowerCase());

const MenusLoadingIllustration = () => (
  <svg
    className="custom-settings-loading-svg"
    viewBox="0 0 120 120"
    width="96"
    height="96"
    aria-hidden="true"
    focusable="false"
  >
    <defs>
      <linearGradient id="cs-loader-grad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#e8f1fc" />
        <stop offset="100%" stopColor="#d6e8fb" />
      </linearGradient>
    </defs>
    <circle
      className="custom-settings-loading-ring"
      cx="60"
      cy="60"
      r="46"
      fill="none"
      stroke="#014694"
      strokeWidth="3"
      strokeLinecap="round"
      strokeDasharray="72 220"
    />
    <rect
      x="28"
      y="28"
      width="64"
      height="64"
      rx="14"
      fill="url(#cs-loader-grad)"
    />
    <g className="custom-settings-loading-bars" fill="#014694">
      <rect className="custom-settings-loading-bar" x="40" y="42" width="40" height="8" rx="4" />
      <rect className="custom-settings-loading-bar" x="40" y="56" width="32" height="8" rx="4" />
      <rect className="custom-settings-loading-bar" x="40" y="70" width="36" height="8" rx="4" />
    </g>
  </svg>
);

const MenusLoadingState = () => (
  <div className="custom-settings-grid-loader" role="status">
    <MenusLoadingIllustration />
    <span>Loading menus...</span>
  </div>
);

const CustomSettings = () => {
  const dispatch = useDispatch<AppDispatch>();
  const menusAndFeatures = useSelector(
    (state: RootState) => state.feature.menusAndFeatures,
  );
  const applicationFeaturesAndMenus = useSelector(
    (state: RootState) => state.feature.applicationFeaturesAndMenus,
  );
  const isLoading = useSelector((state: RootState) => state.feature.isLoading);
  const isMemberFeaturesLoading = useSelector(
    (state: RootState) => state.feature.isMemberFeaturesLoading,
  );
  const isUpdatingFeatureMapping = useSelector(
    (state: RootState) => state.feature.isUpdatingFeatureMapping,
  );
  const pendingMappingChanges = useSelector(
    (state: RootState) => state.feature.pendingMappingChanges,
  );

  const apiMenus = useMemo(
    () => normalizeMenusAndFeatures(menusAndFeatures),
    [menusAndFeatures],
  );
  const dashboardSubsections = useMemo(
    () => mapDashboardSubsections(apiMenus),
    [apiMenus],
  );
  const enabledApplicationMenus = useMemo(() => {
    const loaded = applicationFeaturesAndMenus;
    if (!loaded?.payloads?.length) {
      return [] as ApiMenu[];
    }
    return mergeApplicationFeaturePayloads(
      loaded.payloads,
      normalizeMenusAndFeatures,
    );
  }, [applicationFeaturesAndMenus]);

  const [selectedMemberType, setSelectedMemberType] = useState(
    DEFAULT_MEMBER_TYPE_TAB,
  );
  /** Hide the grid while catalog/features settle after a tab switch */
  const [isTabGridPending, setIsTabGridPending] = useState(true);
  /** Keep page chrome hidden until the first menus load finishes */
  const [hasLoadedShell, setHasLoadedShell] = useState(false);
  const tabLoadSeenRef = useRef(false);
  const [draggingMenuId, setDraggingMenuId] = useState<string | null>(null);
  const [dragOverMenuId, setDragOverMenuId] = useState<string | null>(null);
  const [justDroppedMenuId, setJustDroppedMenuId] = useState<string | null>(
    null,
  );
  const dragMenuIdRef = useRef<string | null>(null);
  const justDroppedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const dragStartPointerRef = useRef<{ x: number; y: number } | null>(null);
  /** Order at drag start — drop swaps only this source with the final target */
  const dragStartSettingsRef = useRef<SettingItem[] | null>(null);

  useEffect(() => {
    const isVisibleTab = VISIBLE_MEMBER_TYPE_FILTERS.some(
      (filter) => filter.id === selectedMemberType,
    );
    if (!isVisibleTab) {
      setSelectedMemberType(DEFAULT_MEMBER_TYPE_TAB);
    }
  }, [selectedMemberType]);

  type TabUiState = {
    settings: SettingItem[];
    subsections: SubsectionState;
    baselineMenus: Record<string, boolean>;
    baselineFeatures: Record<string, boolean>;
    /** Menu id order when tab was loaded — used to detect sequence changes */
    baselineOrderedMenuIds: string[];
    initialized: boolean;
  };

  const createEmptyTabUi = (): TabUiState => ({
    settings: [],
    subsections: {},
    baselineMenus: {},
    baselineFeatures: {},
    baselineOrderedMenuIds: [],
    initialized: false,
  });

  const getOrderedMenuIdStrings = (items: SettingItem[]) =>
    items.map((item) => item.id);

  /**
   * Always send the full visible grid order (enabled + disabled).
   * Enabled-only lists compress positions (e.g. move to 3rd becomes 2nd).
   * UpdateMenuSequence may re-enable listed menus — disabled ones are synced
   * off again after the sequence POST in handleSave.
   */
  const getOrderedMenuIdsForApi = (items: SettingItem[]) =>
    items
      .map((item) => toNumericId(item.id))
      .filter((id): id is number => id != null);

  const getDisabledMenuIdsForApi = (items: SettingItem[]) =>
    items
      .filter((item) => !item.enabled)
      .map((item) => toNumericId(item.id))
      .filter((id): id is number => id != null);

  const getDisabledFeatureIdsForApi = (featureState: SubsectionState) =>
    Object.entries(featureState)
      .filter(([, enabled]) => !enabled)
      .map(([id]) => toNumericId(id))
      .filter((id): id is number => id != null);

  const isTabSequenceDirty = (tab: TabUiState) => {
    if (!tab.initialized) return false;
    const current = getOrderedMenuIdStrings(tab.settings);
    const baseline = tab.baselineOrderedMenuIds;
    return (
      current.length !== baseline.length ||
      current.some((id, index) => id !== baseline[index])
    );
  };

  /** Each tab keeps its own toggle state (All vs Medicare stay independent) */
  const [tabUiById, setTabUiById] = useState<Record<string, TabUiState>>({});
  const [draftSubsections, setDraftSubsections] = useState<SubsectionState>({});
  const [openSubsectionId, setOpenSubsectionId] = useState<string | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const lastApplicationFeaturesRequestRef = useRef<string | null>(null);
  const subsectionsSnapshotRef = useRef<SubsectionState>({});
  const pendingMappingChangesRef = useRef(pendingMappingChanges);
  pendingMappingChangesRef.current = pendingMappingChanges;
  const tabUiByIdRef = useRef(tabUiById);
  tabUiByIdRef.current = tabUiById;

  const currentTabUi = tabUiById[selectedMemberType] ?? createEmptyTabUi();
  const settings = currentTabUi.settings;
  const subsections = currentTabUi.subsections;
  const hasPendingMappingChanges =
    Object.keys(pendingMappingChanges).length > 0;
  const hasPendingSequenceChanges = Object.values(tabUiById).some((tab) =>
    isTabSequenceDirty(tab),
  );
  const hasPendingChanges =
    hasPendingMappingChanges || hasPendingSequenceChanges;

  useEffect(() => {
    // Wait until a load cycle for this tab has started and finished, and UI is ready
    if (isLoading || isMemberFeaturesLoading) {
      tabLoadSeenRef.current = true;
      return;
    }
    if (!tabLoadSeenRef.current) {
      return;
    }
    if (!tabUiByIdRef.current[selectedMemberType]?.initialized) {
      return;
    }
    tabLoadSeenRef.current = false;
    setIsTabGridPending(false);
  }, [
    isLoading,
    isMemberFeaturesLoading,
    selectedMemberType,
    apiMenus,
    applicationFeaturesAndMenus,
    tabUiById,
  ]);

  const beginTabGridLoad = () => {
    tabLoadSeenRef.current = false;
    setIsTabGridPending(true);
  };

  const clearAllTabUi = () => {
    beginTabGridLoad();
    setTabUiById({});
    tabUiByIdRef.current = {};
    setDraggingMenuId(null);
    setDragOverMenuId(null);
    setJustDroppedMenuId(null);
    dragMenuIdRef.current = null;
    dragStartPointerRef.current = null;
    dragStartSettingsRef.current = null;
    if (justDroppedTimerRef.current) {
      clearTimeout(justDroppedTimerRef.current);
      justDroppedTimerRef.current = null;
    }
  };

  const setTabUi = (tabId: string, next: TabUiState) => {
    setTabUiById((prev) => {
      const updated = { ...prev, [tabId]: next };
      tabUiByIdRef.current = updated;
      return updated;
    });
  };

  const patchCurrentTabUi = (patch: (prev: TabUiState) => TabUiState) => {
    setTabUiById((prev) => {
      const current = prev[selectedMemberType] ?? createEmptyTabUi();
      const updated = {
        ...prev,
        [selectedMemberType]: patch(current),
      };
      tabUiByIdRef.current = updated;
      return updated;
    });
  };

  const allSubsectionsSelected =
    dashboardSubsections.length > 0 &&
    dashboardSubsections.every((item) => draftSubsections[item.id]);

  const buildApplicationFeaturesRequestKey = (
    tabId: string,
    memberTypes: string[],
    menus: ApiMenu[],
  ) =>
    `${tabId}::${memberTypes.join(",")}::${menus.map((menu) => menu.id).join(",")}`;

  /**
   * GetAllMenusAndFeatures takes an optional member type for saved sequence.
   * All tab omits member type; specific tabs pass that type only.
   */
  const resolveCatalogMemberType = (tabId: string) =>
    resolveMemberTypesForTab(tabId, isProminenceClient)[0];

  const loadCatalogForTab = (tabId: string) =>
    dispatch(getAllMenusAndFeatures(resolveCatalogMemberType(tabId)));

  const loadFeaturesForTab = (tabId: string) => {
    const memberTypes = resolveMemberTypesForGet(tabId, isProminenceClient);
    const requestKey = buildApplicationFeaturesRequestKey(
      tabId,
      memberTypes,
      apiMenus,
    );
    lastApplicationFeaturesRequestRef.current = requestKey;
    return dispatch(getApplicationFeaturesAndMenus(memberTypes));
  };

  const recordMenuToggle = (menuId: string, nextEnabled: boolean) => {
    const entityId = toNumericId(menuId);
    if (entityId == null) return;

    const memberType = resolveMemberTypesForTab(
      selectedMemberType,
      isProminenceClient,
    );
    const tabState = tabUiByIdRef.current[selectedMemberType] ?? createEmptyTabUi();
    const baselineEnabled = !!tabState.baselineMenus[menuId];

    if (nextEnabled === baselineEnabled) {
      dispatch(
        REMOVE_PENDING_MAPPING_CHANGE({
          entityType: "menu",
          entityId,
          sourceTabId: selectedMemberType,
        }),
      );
      return;
    }

    dispatch(
      UPSERT_PENDING_MAPPING_CHANGE({
        entityType: "menu",
        entityId,
        isDeleted: !nextEnabled,
        memberType,
        sourceTabId: selectedMemberType,
      }),
    );
  };

  const recordFeatureToggle = (featureId: string, nextEnabled: boolean) => {
    const entityId = toNumericId(featureId);
    if (entityId == null) return;

    const memberType = resolveMemberTypesForTab(
      selectedMemberType,
      isProminenceClient,
    );
    const tabState = tabUiByIdRef.current[selectedMemberType] ?? createEmptyTabUi();
    const baselineEnabled = !!tabState.baselineFeatures[featureId];

    if (nextEnabled === baselineEnabled) {
      dispatch(
        REMOVE_PENDING_MAPPING_CHANGE({
          entityType: "feature",
          entityId,
          sourceTabId: selectedMemberType,
        }),
      );
      return;
    }

    dispatch(
      UPSERT_PENDING_MAPPING_CHANGE({
        entityType: "feature",
        entityId,
        isDeleted: !nextEnabled,
        memberType,
        sourceTabId: selectedMemberType,
      }),
    );
  };

  const applyTabUiFromApiData = (
    tabId: string,
    enabledMenus: ApiMenu[],
    pendingChanges: Record<string, PendingMappingChange>,
    catalogMenus: ApiMenu[] = apiMenus,
  ) => {
    const {
      enabledMenuIds,
      enabledMenuLabels,
      enabledFeatureIds,
      enabledFeatureLabels,
    } = collectEnabledKeys(enabledMenus);

    // Catalog (GetAllMenusAndFeatures): render ALL menus + sequence/order
    // ApplicationFeaturesAndMenus: toggle on/off only (presence => enabled)
    const catalogSubsections = mapDashboardSubsections(catalogMenus);
    const baseSettings = mapMenusToSettings(
      catalogMenus,
      enabledMenuIds,
      enabledMenuLabels,
    );
    const baseSubsections = buildSubsectionState(
      catalogSubsections,
      enabledFeatureIds,
      enabledFeatureLabels,
    );

    const baselineMenus = baseSettings.reduce<Record<string, boolean>>(
      (acc, item) => {
        acc[item.id] = item.enabled;
        return acc;
      },
      {},
    );
    const baselineFeatures = { ...baseSubsections };

    const { settings: nextSettings, subsections: nextSubsections } =
      applyPendingOverlayForTab(
        tabId,
        baseSettings,
        baseSubsections,
        pendingChanges,
      );

    const orderedSettings = pinDashboardFirst(
      sortSettingsByApiMenuOrder(nextSettings, catalogMenus),
    );

    setTabUi(tabId, {
      settings: orderedSettings,
      subsections: nextSubsections,
      baselineMenus,
      baselineFeatures,
      baselineOrderedMenuIds: getOrderedMenuIdStrings(orderedSettings),
      initialized: true,
    });

    if (tabId === selectedMemberType) {
      setDraftSubsections(nextSubsections);
      setOpenSubsectionId(null);
    }
  };

  /** Refresh both catalog (all menus + sequence) and enabled mapping for a tab */
  const refreshGridForTab = async (tabId: string) => {
    lastApplicationFeaturesRequestRef.current = null;

    const [catalogResult, appResult] = await Promise.all([
      loadCatalogForTab(tabId),
      loadFeaturesForTab(tabId),
    ]);

    if (
      !getAllMenusAndFeatures.fulfilled.match(catalogResult) ||
      !getApplicationFeaturesAndMenus.fulfilled.match(appResult)
    ) {
      return false;
    }

    const catalogMenus = normalizeMenusAndFeatures(catalogResult.payload);
    const enabledMenus = mergeApplicationFeaturePayloads(
      appResult.payload.payloads,
      normalizeMenusAndFeatures,
    );

    applyTabUiFromApiData(tabId, enabledMenus, {}, catalogMenus);
    return true;
  };

  useEffect(() => {
    // Catalog order is per member type, so reload it whenever the tab changes
    loadCatalogForTab(selectedMemberType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, selectedMemberType]);

  useEffect(() => {
    if (!apiMenus.length) {
      return;
    }

    const memberTypes = resolveMemberTypesForGet(
      selectedMemberType,
      isProminenceClient,
    );
    const requestKey = buildApplicationFeaturesRequestKey(
      selectedMemberType,
      memberTypes,
      apiMenus,
    );

    // Tab already has its own state — keep toggles, but resync order from catalog
    const existingTab = tabUiByIdRef.current[selectedMemberType];
    if (existingTab?.initialized) {
      lastApplicationFeaturesRequestRef.current = requestKey;

      const reorderedSettings = pinDashboardFirst(
        sortSettingsByApiMenuOrder(existingTab.settings, apiMenus),
      );
      const orderChanged = reorderedSettings.some(
        (item, index) => item.id !== existingTab.settings[index]?.id,
      );

      if (orderChanged) {
        setTabUi(selectedMemberType, {
          ...existingTab,
          settings: reorderedSettings,
          baselineOrderedMenuIds: getOrderedMenuIdStrings(reorderedSettings),
        });
      }

      setDraftSubsections(existingTab.subsections);
      setOpenSubsectionId(null);
      return;
    }

    if (lastApplicationFeaturesRequestRef.current === requestKey) {
      return;
    }

    lastApplicationFeaturesRequestRef.current = requestKey;

    const loadedTypesKey =
      applicationFeaturesAndMenus?.memberTypes?.join(",") ?? "";
    const canReuseLoadedApiData =
      loadedTypesKey === memberTypes.join(",") &&
      Boolean(applicationFeaturesAndMenus?.payloads?.length);

    if (canReuseLoadedApiData) {
      applyTabUiFromApiData(
        selectedMemberType,
        enabledApplicationMenus,
        pendingMappingChangesRef.current,
      );
      return;
    }

    loadFeaturesForTab(selectedMemberType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiMenus, dashboardSubsections, selectedMemberType, dispatch]);

  useEffect(() => {
    if (!apiMenus.length || applicationFeaturesAndMenus == null) {
      return;
    }

    // Initialize only the active tab from API; never overwrite other tabs' local toggles
    if (tabUiByIdRef.current[selectedMemberType]?.initialized) {
      return;
    }

    // Do not seed this tab from another tab's GET (e.g. Commercial payload while on ASO).
    // That was causing Commercial saves to appear on ASO after switching tabs.
    const expectedTypes = resolveMemberTypesForGet(
      selectedMemberType,
      isProminenceClient,
    );
    const loadedTypes = applicationFeaturesAndMenus.memberTypes ?? [];
    const typesMatch =
      expectedTypes.length === loadedTypes.length &&
      expectedTypes.every((type, index) => type === loadedTypes[index]);

    if (!typesMatch) {
      return;
    }

    applyTabUiFromApiData(
      selectedMemberType,
      enabledApplicationMenus,
      pendingMappingChangesRef.current,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    apiMenus,
    applicationFeaturesAndMenus,
    dashboardSubsections,
    enabledApplicationMenus,
    selectedMemberType,
    isProminenceClient,
  ]);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        openSubsectionId &&
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node)
      ) {
        setOpenSubsectionId(null);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [openSubsectionId]);

  const toggleSetting = (id: string) => {
    const current = settings.find((item) => item.id === id);
    if (!current) return;

    const nextEnabled = !current.enabled;

    patchCurrentTabUi((prev) => ({
      ...prev,
      settings: prev.settings.map((item) =>
        item.id === id ? { ...item, enabled: nextEnabled } : item,
      ),
      initialized: true,
    }));
    recordMenuToggle(id, nextEnabled);

    if (!nextEnabled) {
      setOpenSubsectionId(null);
    }
  };

  const openSubsections = (item: SettingItem) => {
    if (!item.enabled) {
      return;
    }

    subsectionsSnapshotRef.current = { ...subsections };
    setDraftSubsections(subsections);
    setOpenSubsectionId((current) => (current === item.id ? null : item.id));
  };

  const toggleSelectAll = () => {
    const nextValue = !allSubsectionsSelected;
    const nextState = dashboardSubsections.reduce<SubsectionState>(
      (acc, item) => {
        acc[item.id] = nextValue;
        return acc;
      },
      {},
    );

    setDraftSubsections(nextState);
    patchCurrentTabUi((prev) => ({
      ...prev,
      subsections: { ...prev.subsections, ...nextState },
      initialized: true,
    }));
    dashboardSubsections.forEach((item) => {
      recordFeatureToggle(item.id, nextValue);
    });
  };

  const toggleSubsection = (id: string) => {
    const nextEnabled = !draftSubsections[id];

    setDraftSubsections((prev) => ({
      ...prev,
      [id]: nextEnabled,
    }));
    patchCurrentTabUi((prev) => ({
      ...prev,
      subsections: {
        ...prev.subsections,
        [id]: nextEnabled,
      },
      initialized: true,
    }));
    recordFeatureToggle(id, nextEnabled);
  };

  const applySubsections = async () => {
    patchCurrentTabUi((prev) => ({
      ...prev,
      subsections: { ...draftSubsections },
      initialized: true,
    }));
    // Persist with the same flow as the page "Save Changes" button
    await handleSave();
  };

  const cancelSubsections = () => {
    const snapshot = subsectionsSnapshotRef.current;
    dashboardSubsections.forEach((item) => {
      recordFeatureToggle(item.id, !!snapshot[item.id]);
    });
    patchCurrentTabUi((prev) => ({
      ...prev,
      subsections: { ...snapshot },
      initialized: true,
    }));
    setDraftSubsections(snapshot);
    setOpenSubsectionId(null);
  };

  const reorderMenus = (
    sourceId: string,
    targetId: string,
    baseSettings?: SettingItem[] | null,
  ) => {
    if (!sourceId || !targetId || sourceId === targetId) {
      return false;
    }

    const currentSettings =
      baseSettings ??
      tabUiByIdRef.current[selectedMemberType]?.settings ??
      settings;
    const sourceItem = currentSettings.find((item) => item.id === sourceId);
    const targetItem = currentSettings.find((item) => item.id === targetId);

    // Dashboard sequence is fixed — cannot drag it or drop onto it
    if (
      !sourceItem ||
      !targetItem ||
      isDashboardMenu(sourceItem.label) ||
      isDashboardMenu(targetItem.label)
    ) {
      return false;
    }

    const fromIndex = currentSettings.findIndex((item) => item.id === sourceId);
    const toIndex = currentSettings.findIndex((item) => item.id === targetId);
    if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) {
      return false;
    }

    // Always swap the two menus (vertical / horizontal / diagonal).
    // Do not insert-shift neighbors (e.g. 4 <-> 7 must not move 5/6).
    const nextSettings = pinDashboardFirst(
      (() => {
        const list = [...currentSettings];
        const temp = list[fromIndex];
        list[fromIndex] = list[toIndex];
        list[toIndex] = temp;
        return list;
      })(),
    );

    const unchanged =
      nextSettings.length === currentSettings.length &&
      nextSettings.every(
        (item, index) => item.id === currentSettings[index]?.id,
      );
    if (unchanged) {
      return false;
    }

    patchCurrentTabUi((prev) => ({
      ...prev,
      settings: nextSettings,
      initialized: true,
    }));

    return true;
  };

  const handleMenuDragStart = (
    event: React.DragEvent<HTMLElement>,
    menuId: string,
    label: string,
  ) => {
    if (isDashboardMenu(label)) {
      event.preventDefault();
      return;
    }

    dragMenuIdRef.current = menuId;
    dragStartPointerRef.current = { x: event.clientX, y: event.clientY };
    dragStartSettingsRef.current = [
      ...(tabUiByIdRef.current[selectedMemberType]?.settings ?? settings),
    ];
    setDraggingMenuId(menuId);
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", menuId);

    const row = event.currentTarget.closest(
      ".custom-settings-row",
    ) as HTMLElement | null;
    if (row) {
      const rect = row.getBoundingClientRect();
      const offsetX = Math.max(0, event.clientX - rect.left);
      const offsetY = Math.max(0, event.clientY - rect.top);
      event.dataTransfer.setDragImage(row, offsetX, offsetY);
    }
  };

  const resolveDragTargetId = (
    hoveredId: string,
    clientX: number,
    clientY: number,
  ) => {
    const sourceId = dragMenuIdRef.current;
    const currentSettings =
      dragStartSettingsRef.current ??
      tabUiByIdRef.current[selectedMemberType]?.settings ??
      settings;
    const startPointer = dragStartPointerRef.current;
    const movement = startPointer
      ? { dx: clientX - startPointer.x, dy: clientY - startPointer.y }
      : { dx: 0, dy: 0 };

    if (!sourceId) {
      return hoveredId;
    }

    return resolveGridDragTargetId(
      currentSettings,
      sourceId,
      hoveredId,
      movement,
    );
  };

  const handleMenuDragOver = (
    event: React.DragEvent<HTMLElement>,
    menuId: string,
    label: string,
  ) => {
    event.preventDefault();

    if (isDashboardMenu(label)) {
      event.dataTransfer.dropEffect = "none";
      return;
    }

    event.dataTransfer.dropEffect = "move";

    const targetId = resolveDragTargetId(
      menuId,
      event.clientX,
      event.clientY,
    );

    if (dragOverMenuId !== targetId) {
      setDragOverMenuId(targetId);
    }
  };

  const handleMenuDrop = (
    event: React.DragEvent<HTMLElement>,
    hoveredMenuId: string,
    targetLabel: string,
  ) => {
    event.preventDefault();
    if (isDashboardMenu(targetLabel)) {
      handleMenuDragEnd();
      return;
    }

    const sourceId =
      dragMenuIdRef.current || event.dataTransfer.getData("text/plain");
    const targetMenuId = resolveDragTargetId(
      hoveredMenuId,
      event.clientX,
      event.clientY,
    );

    if (sourceId) {
      setJustDroppedMenuId(sourceId);
      if (justDroppedTimerRef.current) {
        clearTimeout(justDroppedTimerRef.current);
      }
      justDroppedTimerRef.current = setTimeout(() => {
        setJustDroppedMenuId(null);
        justDroppedTimerRef.current = null;
      }, 360);
    }

    // Swap only the dragged menu with the drop target (no path-of-travel swaps)
    reorderMenus(sourceId, targetMenuId, dragStartSettingsRef.current);

    setDraggingMenuId(null);
    setDragOverMenuId(null);
    dragMenuIdRef.current = null;
    dragStartPointerRef.current = null;
    dragStartSettingsRef.current = null;
  };

  const handleMenuDragEnd = () => {
    setDraggingMenuId(null);
    setDragOverMenuId(null);
    dragMenuIdRef.current = null;
    dragStartPointerRef.current = null;
    dragStartSettingsRef.current = null;
  };

  const handleReset = async () => {
    setOpenSubsectionId(null);
    dispatch(CLEAR_PENDING_MAPPING_CHANGES());
    clearAllTabUi();

    await refreshGridForTab(selectedMemberType);
  };

  const handleSave = async () => {
    setOpenSubsectionId(null);

    const mappingChanges = Object.values(pendingMappingChanges);
    const sequenceDirtyTabs = Object.entries(tabUiByIdRef.current).filter(
      ([, tab]) => isTabSequenceDirty(tab),
    );

    if (!mappingChanges.length && !sequenceDirtyTabs.length) {
      return;
    }

    const clientId = Number(process.env.REACT_APP_PAYER_ID);
    if (!clientId || Number.isNaN(clientId)) {
      console.error("Invalid REACT_APP_PAYER_ID for feature mapping update");
      return;
    }

    try {
      // When both change: mapping first, then sequence
      if (mappingChanges.length) {
        const payloads = groupPendingChangesForSave(mappingChanges, clientId);
        for (const payload of payloads) {
          await dispatch(updateFeatureClientMapping(payload)).unwrap();
        }
        dispatch(CLEAR_PENDING_MAPPING_CHANGES());
      }

      for (const [tabId, tab] of sequenceDirtyTabs) {
        const orderedMenuIds = getOrderedMenuIdsForApi(tab.settings);
        if (!orderedMenuIds.length) continue;

        // Must match UpdateMenuSequence memberType — All tab defaults to
        // Medicare, and a mismatched restore leaves menus re-enabled (ANC-5050).
        const sequenceMemberType = resolveMemberTypesForSequenceSave(
          tabId,
          isProminenceClient,
        );

        await dispatch(
          updateMenuSequence({
            clientId,
            memberType: sequenceMemberType,
            orderedMenuIds,
          }),
        ).unwrap();

        // Sequence upsert can turn listed menus/features back on — restore off
        // state with the same memberType used for the sequence POST.
        const disabledMenuIds = getDisabledMenuIdsForApi(tab.settings);
        const disabledFeatureIds = getDisabledFeatureIdsForApi(tab.subsections);
        if (disabledMenuIds.length || disabledFeatureIds.length) {
          await dispatch(
            updateFeatureClientMapping({
              clientId,
              menuId: disabledMenuIds,
              featureIds: disabledFeatureIds,
              isDeleted: true,
              memberType: sequenceMemberType,
            }),
          ).unwrap();
        }
      }

      clearAllTabUi();

      // Refresh both GETs: catalog (all menus + sequence) and enabled mapping
      await refreshGridForTab(selectedMemberType);
    } catch (error) {
      console.error("Failed to save custom settings:", error);
    }
  };

  const renderSettingRow = (item: SettingItem) => {
    const hasDashboardSubsections =
      isDashboardMenu(item.label) && dashboardSubsections.length > 0;
    const canManageSubsections = hasDashboardSubsections && item.enabled;
    const isPopoverOpen = openSubsectionId === item.id && canManageSubsections;
    const isDashboard = isDashboardMenu(item.label);
    const sequence =
      settings.findIndex((setting) => setting.id === item.id) + 1;
    const isDragging = draggingMenuId === item.id;
    const isDragOver =
      !isDashboard &&
      dragOverMenuId === item.id &&
      draggingMenuId !== item.id;
    const isJustDropped = justDroppedMenuId === item.id;

    return (
      <div
        key={item.id}
        className={`col-12 col-md-6 col-xl-4 custom-settings-grid-item ${
          isDragging ? "is-dragging-item" : ""
        } ${isDragOver ? "is-drag-over-item" : ""} ${
          isPopoverOpen ? "is-popover-open" : ""
        }`}
        onDragOver={(event) =>
          handleMenuDragOver(event, item.id, item.label)
        }
        onDrop={(event) => handleMenuDrop(event, item.id, item.label)}
      >
        <div
          className={`custom-settings-row ${
            item.enabled ? "is-enabled" : ""
          } ${isDragging ? "is-dragging" : ""} ${
            isDragOver ? "is-drag-over" : ""
          } ${isJustDropped ? "is-just-dropped" : ""} ${
            isDashboard ? "is-sequence-locked" : ""
          } ${isPopoverOpen ? "is-popover-open" : ""}`}
          role="button"
          tabIndex={0}
          aria-pressed={item.enabled}
          aria-label={`${item.enabled ? "Disable" : "Enable"} ${item.label}`}
          onClick={() => toggleSetting(item.id)}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              toggleSetting(item.id);
            }
          }}
        >
          <span className="custom-settings-sequence" title="Sequence">
            {sequence}
          </span>

          <div className="custom-settings-row-main">
            <div className="custom-settings-row-info">
              <span className="custom-settings-row-icon">
                <i className={item.icon}></i>
              </span>
              <span className="custom-settings-row-label">{item.label}</span>
            </div>

            <div className="custom-settings-row-actions">
              {!isDashboard && (
                <span
                  className="custom-settings-drag-handle"
                  draggable
                  role="button"
                  tabIndex={0}
                  aria-label={`Drag to reorder ${item.label}`}
                  title="Drag to reorder"
                  onClick={(event) => event.stopPropagation()}
                  onKeyDown={(event) => event.stopPropagation()}
                  onDragStart={(event) =>
                    handleMenuDragStart(event, item.id, item.label)
                  }
                  onDragEnd={handleMenuDragEnd}
                >
                  <span className="custom-settings-drag-grip" aria-hidden="true">
                    ⠿
                  </span>
                </span>
              )}

              {canManageSubsections && (
                <div
                  className="custom-settings-subsections"
                  ref={isPopoverOpen ? popoverRef : null}
                  onClick={(event) => event.stopPropagation()}
                  onKeyDown={(event) => event.stopPropagation()}
                >
                  <button
                    type="button"
                    className={`custom-settings-more-btn ${
                      isPopoverOpen ? "is-open" : ""
                    }`}
                    onClick={() => openSubsections(item)}
                    data-tooltip-id="manage-subsections-tooltip"
                    data-tooltip-content="Manage Subsections"
                    data-tooltip-place="top"
                    aria-label="Manage Subsections"
                  >
                    <i className="fh_more_vertical"></i>
                  </button>

                  {isPopoverOpen && (
                    <div className="custom-settings-popover">
                      <label className="custom-settings-check">
                        <input
                          type="checkbox"
                          checked={allSubsectionsSelected}
                          onChange={toggleSelectAll}
                        />
                        <span>Select All</span>
                      </label>

                      {dashboardSubsections.map((sub) => (
                        <label key={sub.id} className="custom-settings-check">
                          <input
                            type="checkbox"
                            checked={!!draftSubsections[sub.id]}
                            onChange={() => toggleSubsection(sub.id)}
                          />
                          <span>{sub.label}</span>
                        </label>
                      ))}

                      <div className="custom-settings-popover-actions">
                        <button
                          type="button"
                          className="btn btn-border-primary"
                          onClick={cancelSubsections}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={applySubsections}
                          disabled={
                            isLoading ||
                            isMemberFeaturesLoading ||
                            isUpdatingFeatureMapping ||
                            !hasPendingMappingChanges
                          }
                        >
                          {isUpdatingFeatureMapping ? "Saving..." : "Apply"}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const showLoading =
    isTabGridPending ||
    isLoading ||
    isMemberFeaturesLoading ||
    !currentTabUi.initialized;

  useEffect(() => {
    if (!showLoading) {
      setHasLoadedShell(true);
    }
  }, [showLoading]);

  const showPageLoader = showLoading && !hasLoadedShell;

  return (
    <div className="main-content custom-settings-page">
      <div className="content-header d-flex justify-content-between align-items-center flex-wrap gap-3">
        <h1 className="page-header mb-0">Custom Settings</h1>
        <div className="d-flex align-items-center gap-2 flex-wrap custom-settings-actions">
          <button
            type="button"
            className="btn btn-border-primary h-48"
            onClick={handleReset}
            disabled={
              showPageLoader ||
              isLoading ||
              isMemberFeaturesLoading ||
              isUpdatingFeatureMapping ||
              isTabGridPending ||
              !hasPendingChanges
            }
          >
            Reset
          </button>
          <button
            type="button"
            className="btn btn-primary h-48"
            onClick={handleSave}
            disabled={
              showPageLoader ||
              isLoading ||
              isMemberFeaturesLoading ||
              isUpdatingFeatureMapping ||
              isTabGridPending ||
              !hasPendingChanges
            }
          >
            {isUpdatingFeatureMapping ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>

      <div className="card custom-settings-card">
        <div className="card-body">
          {showPageLoader ? (
            <MenusLoadingState />
          ) : (
            <>
              <div className="custom-settings-info-banner">
                <i className="fh_info_fill"></i>
                <p className="mb-0">
                Select or deselect to enable and disable sections, drag menus to set sequence, and manage subsections for Member Portal visibility.
                </p>
              </div>

              {VISIBLE_MEMBER_TYPE_FILTERS.length > 1 && (
                <div
                  className="custom-settings-member-filters"
                  role="tablist"
                  aria-label="Member type"
                >
                  {VISIBLE_MEMBER_TYPE_FILTERS.map((filter) => (
                    <button
                      key={filter.id}
                      type="button"
                      role="tab"
                      aria-selected={selectedMemberType === filter.id}
                      className={`custom-settings-member-chip ${
                        selectedMemberType === filter.id ? "active" : ""
                      }`}
                      onClick={() => {
                        if (filter.id === selectedMemberType) return;
                        beginTabGridLoad();
                        setSelectedMemberType(filter.id);
                        setOpenSubsectionId(null);
                      }}
                    >
                      {filter.label}
                    </button>
                  ))}
                </div>
              )}

              <div className="row custom-settings-grid">
                {showLoading ? (
                  <div className="col-12">
                    <MenusLoadingState />
                  </div>
                ) : settings.length === 0 ? (
                  <div className="col-12 text-muted">No menus available.</div>
                ) : (
                  settings.map(renderSettingRow)
                )}
              </div>
            </>
          )}
        </div>
      </div>

      <Tooltip id="manage-subsections-tooltip" place="top" />
    </div>
  );
};

export default CustomSettings;
